import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { rolesAPI } from '../services/api';

interface RoleOption {
  roleId: number;
  roleName: string;
}

const Register: React.FC = () => {
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    login: '',
    password: '',
    confirmPassword: '',
    roleId: 0 as number,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await rolesAPI.getAll();
        if (res.data?.status === 'success' && Array.isArray(res.data.data)) {
          setRoles(res.data.data);
          if (res.data.data.length > 0) {
            setFormData(prev => ({ ...prev, roleId: res.data.data[0].roleId }));
          }
        }
      } catch (e) {
      }
    };
    loadRoles();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!formData.roleId) {
      setError('Please select a role');
      return;
    }

    setIsLoading(true);

    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        login: formData.login,
        password: formData.password,
        roleId: formData.roleId,
      };

      await register(userData);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#616161',
        padding: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 5,
          width: '100%',
          maxWidth: 500,
          borderRadius: 3,
          backgroundColor: '#FFFFFF',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
          <Typography 
            component="h1" 
            variant="h3" 
            sx={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}
          >
            Sign Up
          </Typography>
        </Box>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField
              required
              fullWidth
              name="firstName"
              label="First Name"
              value={formData.firstName}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
            <TextField
              required
              fullWidth
              name="lastName"
              label="Last Name"
              value={formData.lastName}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
            <TextField
              required
              fullWidth
              name="login"
              label="Login"
              type="text"
              value={formData.login}
              onChange={handleChange}
              sx={{ 
                gridColumn: { xs: '1', sm: '1 / -1' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
            <FormControl fullWidth required sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <InputLabel>Role</InputLabel>
              <Select
                name="roleId"
                value={formData.roleId}
                label="Role"
                onChange={(e) => setFormData(prev => ({ ...prev, roleId: Number(e.target.value) }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '1rem',
                    '&.Mui-focused fieldset': { borderColor: '#CA4300', borderWidth: 2 },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#CA4300' },
                }}
              >
                {roles.map((r) => (
                  <MenuItem key={r.roleId} value={r.roleId}>
                    {r.roleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
            <TextField
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
          </Box>
            
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                fontSize: '1rem',
                fontWeight: 'bold',
                px: 3,
                py: 1.5,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ 
                backgroundColor: '#CA4300',
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 'bold',
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#A03600'
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </Box>
          
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link href="/login" variant="body2" sx={{ color: '#CA4300' }}>
              {"Already have an account? Sign In"}
            </Link>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Register;
